import React, { useEffect, useState } from 'react';
import { Drawer, Layout, Menu } from 'antd';
import {
  AuditOutlined,
  BankOutlined,
  BellOutlined,
  BookOutlined,
  CalendarOutlined,
  CloseOutlined,
  CloudServerOutlined,
  ContactsOutlined,
  ContainerOutlined,
  CrownOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  FormOutlined,
  GlobalOutlined,
  KeyOutlined,
  MessageOutlined,
  PlayCircleOutlined,
  QrcodeOutlined,
  ReadOutlined,
  RobotOutlined,
  ScheduleOutlined,
  SettingOutlined,
  ShopOutlined,
  SolutionOutlined,
  StarOutlined,
  TableOutlined,
  TeamOutlined,
  TrophyOutlined,
  UnorderedListOutlined,
  UserOutlined,
  UsergroupAddOutlined,
  VideoCameraOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useResponsive from '../../hooks/useResponsive';
import { fixedManagementHome } from '../ManagementLanding';

const { Sider } = Layout;

const AppSidebar = ({ mobileMenuVisible, onMobileMenuClose }) => {
  const [selectedKeys, setSelectedKeys] = useState(['dashboard']);
  const [openKeys, setOpenKeys] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isMobile } = useResponsive();

  const isPlatformAdmin = Boolean(user && (
    user.username === 'admin' ||
    user.role === 'super_admin' ||
    user.role === 'platform_admin'
  ));
  const isSchoolAdmin = user?.role === 'school_admin';
  const isTeacher = user?.role === 'teacher';

  const showSchoolMenu = Boolean(user && (isPlatformAdmin || isSchoolAdmin || isTeacher));
  const showUserMenu = Boolean(user && (isPlatformAdmin || isSchoolAdmin));
  const showAIMenu = Boolean(user && (isPlatformAdmin || isSchoolAdmin || isTeacher));
  const showCorrectionSubmitMenu = Boolean(user && (isPlatformAdmin || isSchoolAdmin || isTeacher));
  const showCorrectionReviewMenu = Boolean(user && (isPlatformAdmin || isSchoolAdmin));
  const showSchoolInviteMenu = Boolean(user && (isPlatformAdmin || isSchoolAdmin));
  const showActivityScanMenu = Boolean(user && (isPlatformAdmin || isSchoolAdmin));

  useEffect(() => {
    const path = location.pathname;
    const setKey = (key, parentKey) => {
      setSelectedKeys([key]);
      if (parentKey) setOpenKeys((prev) => prev.includes(parentKey) ? prev : [...prev, parentKey]);
    };

    if (path === '/' || path === '/dashboard') setKey('dashboard');
    else if (path.startsWith('/school-admin-registrations')) setKey('school-admin-registrations', 'school-management');
    else if (path.startsWith('/school-admin')) setKey('school-admin', 'school-management');
    else if (path.startsWith('/grade-management')) setKey('grade-management', 'school-management');
    else if (path.startsWith('/school-operations')) setKey('school-operations', 'school-management');
    else if (path.startsWith('/school-affiliation-corrections/review')) setKey('school-affiliation-correction-review', 'school-management');
    else if (path.startsWith('/school-affiliation-corrections')) setKey('school-affiliation-correction-submit', 'school-management');
    else if (path.startsWith('/schools')) setKey('schools', 'school-management');
    else if (path.startsWith('/admins')) setKey('admins', 'school-management');
    else if (path.startsWith('/users/parents')) setKey('users-parents', 'users');
    else if (path.startsWith('/users/list')) setKey('users-list', 'users');
    else if (path.startsWith('/users')) setKey('users-registrations', 'users');
    else if (path.startsWith('/teacher-code-query')) setKey('teacher-code-query');
    else if (path.startsWith('/teacher-code-admin')) setKey('teacher-code-admin');
    else if (path.startsWith('/teachers')) setKey('teachers');
    else if (path.startsWith('/classes/schedule')) setKey('classes-schedule', 'classes');
    else if (path.startsWith('/classes/duty')) setKey('classes-duty', 'classes');
    else if (path.startsWith('/classes/cadres')) setKey('classes-cadres', 'classes');
    else if (path.startsWith('/classes/todos')) setKey('classes-todos', 'classes');
    else if (path.startsWith('/classes/seat')) setKey('classes-seat', 'classes');
    else if (path.startsWith('/classes/parent-binding')) setKey('classes-binding', 'classes');
    else if (path.startsWith('/classes')) setKey('classes-list', 'classes');
    else if (path.startsWith('/students/points-new')) setKey('student-points-new', 'pet-points-management');
    else if (path.startsWith('/students/pets')) setKey('student-pets', 'pet-points-management');
    else if (path.startsWith('/students/pet-ranking')) setKey('student-pet-ranking', 'pet-points-management');
    else if (path.startsWith('/students/shop')) setKey('student-shop', 'pet-points-management');
    else if (path.startsWith('/students/growth-record')) setKey('student-growth-record', 'student-management');
    else if (path.startsWith('/students/violations')) setKey('student-violations', 'student-management');
    else if (path.startsWith('/students/praise')) setKey('student-praise', 'student-management');
    else if (path.startsWith('/students/home-visits')) setKey('student-home-visits', 'student-management');
    else if (path.startsWith('/students/grades')) setKey('student-grades');
    else if (path.startsWith('/students')) setKey('students-list', 'student-management');
    else if (path.startsWith('/settings/ai-school')) setKey('ai-school', 'ai-management');
    else if (path.startsWith('/settings/ai-platform')) setKey('ai-platform', 'ai-management');
    else if (path.startsWith('/settings/system-ai')) setKey('system-ai', 'ai-management');
    else if (path.startsWith('/settings')) setKey('ai-providers', 'ai-management');
    else if (path.startsWith('/membership') && !path.startsWith('/admin/membership')) setKey('membership', 'ai-management');
    else if (path.startsWith('/admin/membership')) setKey('admin-membership', 'ai-management');
    else if (path.startsWith('/broadcast')) setKey('broadcast');
    else if (path.startsWith('/system-announcements')) setKey('system-announcements');
    else if (path.startsWith('/contacts')) setKey('contacts');
    else if (path.startsWith('/smart-attendance')) setKey('smart-attendance');
    else if (path.startsWith('/question-bank')) setKey('question-bank');
    else if (path.startsWith('/quiz-manager') || path.startsWith('/quiz-control')) setKey('quiz-wireless', 'quiz');
    else if (path.startsWith('/desktop-quiz')) setKey('quiz-desktop', 'quiz');
    else if (path.startsWith('/homework')) setKey('homework-management');
    else if (path.startsWith('/data-collection')) setKey('data-collection');
    else if (path.startsWith('/activity-scan')) setKey('activity-scan');
    else if (path.startsWith('/operator-codes')) setKey('operator-codes');
    else if (path.startsWith('/login-logs')) setKey('login-logs');
    else if (path.startsWith('/user-feedback')) setKey('user-feedback');
    else if (path.startsWith('/system-monitor')) setKey('system-monitor');
    else if (path.startsWith('/system-settings')) setKey('system-settings');
    else if (path.startsWith('/teacher-navigation/review')) setKey('teacher-navigation-review');
    else if (path.startsWith('/teacher-resources/manage')) setKey('teacher-resources-manage');
    else if (path.startsWith('/profile')) setKey('profile');
  }, [location.pathname]);

  const handleMenuClick = (path) => {
    navigate(path);
    if (isMobile && onMobileMenuClose) onMobileMenuClose();
  };

  const menuContent = (
    <Menu
      mode="inline"
      selectedKeys={selectedKeys}
      openKeys={openKeys}
      onOpenChange={setOpenKeys}
      style={{ height: '100%', borderRight: 0 }}
      theme="dark"
    >
      <Menu.Item key="dashboard" icon={<DashboardOutlined />} onClick={() => handleMenuClick(fixedManagementHome(user) || '/dashboard')}>
        首页
      </Menu.Item>

      {showSchoolMenu && (
        <Menu.SubMenu key="school-management" icon={<BankOutlined />} title="学校管理">
          <Menu.Item key="grade-management" icon={<TeamOutlined />} onClick={() => handleMenuClick('/grade-management')}>
            年级管理
          </Menu.Item>
          {(isPlatformAdmin || isSchoolAdmin) && (
            <Menu.Item key="school-admin" icon={<BankOutlined />} onClick={() => handleMenuClick('/school-admin')}>
              学校工作台
            </Menu.Item>
          )}
          {isSchoolAdmin && (
            <Menu.Item key="school-operations" icon={<BellOutlined />} onClick={() => handleMenuClick('/school-operations')}>
              学校通知与任务
            </Menu.Item>
          )}
          {isPlatformAdmin && (
            <Menu.Item key="schools" icon={<UnorderedListOutlined />} onClick={() => handleMenuClick('/schools')}>
              学校列表
            </Menu.Item>
          )}
          {isPlatformAdmin && (
            <Menu.Item key="admins" icon={<TeamOutlined />} onClick={() => handleMenuClick('/admins')}>
              学校管理员管理
            </Menu.Item>
          )}
          {isPlatformAdmin && (
            <Menu.Item key="school-admin-registrations" icon={<AuditOutlined />} onClick={() => handleMenuClick('/school-admin-registrations')}>
              学校管理员注册审核
            </Menu.Item>
          )}
          {showCorrectionSubmitMenu && (
            <Menu.Item key="school-affiliation-correction-submit" icon={<FormOutlined />} onClick={() => handleMenuClick('/school-affiliation-corrections/my')}>
              学校入校申请
            </Menu.Item>
          )}
          {showCorrectionReviewMenu && (
            <Menu.Item key="school-affiliation-correction-review" icon={<AuditOutlined />} onClick={() => handleMenuClick('/school-affiliation-corrections/review')}>
              入校申请审核
            </Menu.Item>
          )}
          {showSchoolInviteMenu && (
            <Menu.Item key="invitation-codes" icon={<QrcodeOutlined />} onClick={() => handleMenuClick('/invitation-codes')}>
              学校邀请码
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}

      {showUserMenu && (
        <Menu.SubMenu key="users" icon={<UsergroupAddOutlined />} title="用户管理">
          <Menu.Item key="users-list" onClick={() => handleMenuClick('/users/list')}>用户列表</Menu.Item>
          <Menu.Item key="users-parents" onClick={() => handleMenuClick('/users/parents')}>家长管理</Menu.Item>
          <Menu.Item key="users-registrations" onClick={() => handleMenuClick('/users')}>注册审核</Menu.Item>
        </Menu.SubMenu>
      )}

      {isPlatformAdmin && (
        <Menu.Item key="teacher-navigation-review" icon={<AuditOutlined />} onClick={() => handleMenuClick('/teacher-navigation/review')}>
          导航内容审核
        </Menu.Item>
      )}

      {isPlatformAdmin && (
        <Menu.Item key="teacher-resources-manage" icon={<BookOutlined />} onClick={() => handleMenuClick('/teacher-resources/manage')}>
          教师资源库管理
        </Menu.Item>
      )}

      {isPlatformAdmin && (
        <Menu.Item key="system-settings" icon={<SettingOutlined />} onClick={() => handleMenuClick('/system-settings')}>
          系统设置
        </Menu.Item>
      )}
      {isPlatformAdmin && (
        <>
        <Menu.Item key="user-feedback" icon={<MessageOutlined />} onClick={() => handleMenuClick('/user-feedback')}>用户反馈</Menu.Item>
        <Menu.Item key="system-monitor" icon={<CloudServerOutlined />} onClick={() => handleMenuClick('/system-monitor')}>
          系统监控
        </Menu.Item>
        </>
      )}

      {showAIMenu && (
        <Menu.SubMenu key="ai-management" icon={<RobotOutlined />} title="AI模型管理">
          <Menu.Item key="ai-providers" icon={<UserOutlined />} onClick={() => handleMenuClick('/settings/ai-providers')}>
            个人AI模型设置
          </Menu.Item>
          {(isPlatformAdmin || isSchoolAdmin) && (
            <Menu.Item key="ai-school" icon={<GlobalOutlined />} onClick={() => handleMenuClick('/settings/ai-school')}>
              学校AI算力池
            </Menu.Item>
          )}
          <Menu.Item key="membership" icon={<CrownOutlined />} onClick={() => handleMenuClick('/membership')}>
            会员中心
          </Menu.Item>
          {isPlatformAdmin && (
            <Menu.Item key="admin-membership" icon={<TeamOutlined />} onClick={() => handleMenuClick('/admin/membership')}>
              会员管理
            </Menu.Item>
          )}
          {isPlatformAdmin && (
            <Menu.Item key="system-ai" icon={<CloudServerOutlined />} onClick={() => handleMenuClick('/settings/system-ai')}>
              系统AI全局配置
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}

      <Menu.Item key="teachers" icon={<ReadOutlined />} onClick={() => handleMenuClick('/teachers')}>教师管理</Menu.Item>
      {isPlatformAdmin && <Menu.Item key="teacher-code-query" icon={<KeyOutlined />} onClick={() => handleMenuClick('/teacher-code-query')}>教师码查询</Menu.Item>}
      {isPlatformAdmin && <Menu.Item key="teacher-code-admin" icon={<KeyOutlined />} onClick={() => handleMenuClick('/teacher-code-admin')}>教师码管理</Menu.Item>}

      <Menu.SubMenu key="classes" icon={<TeamOutlined />} title="班级管理">
        <Menu.Item key="classes-list" icon={<UnorderedListOutlined />} onClick={() => handleMenuClick('/classes')}>班级列表</Menu.Item>
        <Menu.Item key="classes-schedule" icon={<CalendarOutlined />} onClick={() => handleMenuClick('/classes/schedule')}>课程表管理</Menu.Item>
        <Menu.Item key="classes-duty" icon={<ScheduleOutlined />} onClick={() => handleMenuClick('/classes/duty')}>值日生管理</Menu.Item>
        <Menu.Item key="classes-cadres" icon={<TeamOutlined />} onClick={() => handleMenuClick('/classes/cadres')}>班干部管理</Menu.Item>
        <Menu.Item key="classes-todos" icon={<FormOutlined />} onClick={() => handleMenuClick('/classes/todos')}>班级待办事项</Menu.Item>
        <Menu.Item key="classes-seat" icon={<TableOutlined />} onClick={() => handleMenuClick('/classes/seat')}>座位管理</Menu.Item>
        <Menu.Item key="classes-binding" icon={<ContactsOutlined />} onClick={() => handleMenuClick('/classes/parent-binding')}>家长微信绑定管理</Menu.Item>
      </Menu.SubMenu>

      <Menu.SubMenu key="student-management" icon={<SolutionOutlined />} title="学生管理">
        <Menu.Item key="students-list" onClick={() => handleMenuClick('/students')}>学生资料</Menu.Item>
        <Menu.Item key="student-growth-record" onClick={() => handleMenuClick('/students/growth-record')}>成长记录</Menu.Item>
        <Menu.Item key="student-violations" onClick={() => handleMenuClick('/students/violations')}>违纪记录</Menu.Item>
        <Menu.Item key="student-praise" onClick={() => handleMenuClick('/students/praise')}>表扬管理</Menu.Item>
        <Menu.Item key="student-home-visits" onClick={() => handleMenuClick('/students/home-visits')}>家访管理</Menu.Item>
      </Menu.SubMenu>

      <Menu.Item key="smart-attendance" icon={<VideoCameraOutlined />} onClick={() => handleMenuClick('/smart-attendance')}>智能考勤</Menu.Item>
      <Menu.Item key="question-bank" icon={<DatabaseOutlined />} onClick={() => handleMenuClick('/question-bank')}>题库管理</Menu.Item>

      <Menu.SubMenu key="quiz" icon={<FormOutlined />} title="在线答题">
        <Menu.Item key="quiz-wireless" icon={<PlayCircleOutlined />} onClick={() => handleMenuClick('/quiz-manager')}>无线答题</Menu.Item>
        <Menu.Item key="quiz-desktop" icon={<BookOutlined />} onClick={() => handleMenuClick('/desktop-quiz')}>桌面端答题</Menu.Item>
      </Menu.SubMenu>

      <Menu.Item key="student-grades" icon={<FileTextOutlined />} onClick={() => handleMenuClick('/students/grades')}>成绩管理</Menu.Item>
      <Menu.Item key="homework-management" icon={<BookOutlined />} onClick={() => handleMenuClick('/homework')}>作业管理 / 查收</Menu.Item>
      <Menu.Item key="data-collection" icon={<ContainerOutlined />} onClick={() => handleMenuClick('/data-collection')}>数据收集</Menu.Item>

      {showActivityScanMenu && (
        <Menu.Item key="activity-scan" icon={<QrcodeOutlined />} onClick={() => handleMenuClick('/activity-scan')}>活动扫码</Menu.Item>
      )}

      <Menu.SubMenu key="pet-points-management" icon={<StarOutlined />} title="宠物积分管理">
        <Menu.Item key="student-points-new" icon={<TrophyOutlined />} onClick={() => handleMenuClick('/students/points-new')}>积分管理</Menu.Item>
        <Menu.Item key="student-pets" icon={<StarOutlined />} onClick={() => handleMenuClick('/students/pets')}>宠物中心</Menu.Item>
        <Menu.Item key="student-pet-ranking" icon={<TrophyOutlined />} onClick={() => handleMenuClick('/students/pet-ranking')}>宠物排行</Menu.Item>
        <Menu.Item key="student-shop" icon={<ShopOutlined />} onClick={() => handleMenuClick('/students/shop')}>商店中心</Menu.Item>
      </Menu.SubMenu>

      <Menu.Item key="broadcast" icon={<MessageOutlined />} onClick={() => handleMenuClick('/broadcast')}>广播管理</Menu.Item>
      {isPlatformAdmin && (
        <Menu.Item key="system-announcements" icon={<BellOutlined />} onClick={() => handleMenuClick('/system-announcements')}>系统公告</Menu.Item>
      )}
      <Menu.Item key="contacts" icon={<ContactsOutlined />} onClick={() => handleMenuClick('/contacts')}>通讯录管理</Menu.Item>
      <Menu.Item key="operator-codes" icon={<KeyOutlined />} onClick={() => handleMenuClick('/operator-codes')}>操作码管理</Menu.Item>
      {isPlatformAdmin && (
        <Menu.Item key="login-logs" icon={<AuditOutlined />} onClick={() => handleMenuClick('/login-logs')}>登录记录</Menu.Item>
      )}
      <Menu.Item key="profile" icon={<UserOutlined />} onClick={() => handleMenuClick('/profile')}>个人资料</Menu.Item>
    </Menu>
  );

  if (isMobile) {
    return (
      <Drawer
        title="菜单导航"
        placement="left"
        onClose={onMobileMenuClose}
        open={mobileMenuVisible}
        width={260}
        closeIcon={<CloseOutlined style={{ color: '#fff' }} />}
        styles={{ body: { padding: 0, background: '#001529' }, header: { background: '#001529', color: '#fff' } }}
      >
        {menuContent}
      </Drawer>
    );
  }

  return (
    <Sider breakpoint="lg" collapsedWidth="0" style={{ minHeight: '100vh' }}>
      {menuContent}
    </Sider>
  );
};

export default AppSidebar;
